import * as Immutable from "immutable";
import { TypedMap } from "types/global";
import BaseMessageRecord, {
  BaseMessageRecordAttributes,
  BaseMessageRecordInstance
} from "../BaseMessageRecord";

interface ScheduledBlockRecordAttributes extends BaseMessageRecordAttributes {
  type: "scheduled_block",
  scheduleId: string,
  affirmativeMessages: Immutable.List<BaseMessageRecordInstance>,
  negativeMessages: Immutable.List<BaseMessageRecordInstance>
}

export class ScheduledBlockRecord extends BaseMessageRecord<ScheduledBlockRecordAttributes>({
  type: "scheduled_block",
  scheduleId: "",
  affirmativeMessages: Immutable.List(),
  negativeMessages: Immutable.List()
}) {}

/**
 * Validation
 */

ScheduledBlockRecord.getInvalidFields = (message: ScheduledBlockRecord): Immutable.List<string> => {
  const invalidFields: Set<string> = new Set();

  if (!message.scheduleId) {
    invalidFields.add("scheduleId");
  }

  // Check that affirmative and negative messages are non-empty
  if (message.affirmativeMessages.size === 0) {
    invalidFields.add("affirmativeMessages");
  }

  if (message.negativeMessages.size === 0) {
    invalidFields.add("negativeMessages");
  }

  // Make sure there are no scheduled blocks in the scheduled block
  if (
    message.affirmativeMessages.concat(message.negativeMessages)
      .find(m => m.type === "scheduled_block")
  ) {
    invalidFields.add("nestedScheduledBlock");
  }

  // Get invalid fields from nested messages (affirmativeMessages)
  message.affirmativeMessages.forEach((m, index) => {
    m.invalidFields.forEach(field => invalidFields.add(`affirmativeMessages-${index}-${field}`));
  });

  // Get invalid fields from nested messages (negativeMessages)
  message.negativeMessages.forEach((m, index) => {
    m.invalidFields.forEach(field => invalidFields.add(`negativeMessages-${index}-${field}`));
  });

  return Immutable.List(invalidFields);
};

/**
 * Creates ScheduledBlockRecord, using `messageCreator` to create nested messages
 *
 * @param {Immutable.Map|Object} attributes
 * @param {Function} messageCreator - a function to create message instance from raw attributes
 * @class
 */
export default function ScheduledBlockRecordCreator(
  attributes: Record<string, unknown>,
  messageCreator: (message: unknown) => BaseMessageRecordInstance
): ScheduledBlockRecord {
  const attrsMap: TypedMap<{
    affirmativeMessages: unknown[];
    negativeMessages: unknown[];
  }> = Immutable.Map(attributes);

  return new ScheduledBlockRecord({
    ...attrsMap.toJS() as ScheduledBlockRecordAttributes,
    affirmativeMessages: Immutable.List(attrsMap.get("affirmativeMessages")).map(messageCreator),
    negativeMessages: Immutable.List(attrsMap.get("negativeMessages")).map(messageCreator)
  });
}
